import React from 'react';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { LayoutComposer, Page, PageHero } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import AADCalculator from './calculators/AADCalculator';
import AltimeterCalculator from './calculators/AltimeterCalculator';
import HelmetCalculator from './calculators/HelmetCalculator';
import ReserveCalculator from './calculators/ReserveCalculator';
import ContainerCalculator from './calculators/ContainerCalculator';
import MainCanopyCalculator from './calculators/MainCanopyCalculator';
import WingsuitCalculator from './calculators/WingsuitCalculator';

import css from './GearValueCalculatorPage.module.css';

const IMG = '/static/images/gv-calculator';

const CATEGORIES = [
  { id: 'container', num: '01', label: 'Container' },
  { id: 'main-canopy', num: '02', label: 'Main Canopy' },
  { id: 'reserve', num: '03', label: 'Reserve' },
  { id: 'aad', num: '04', label: 'AAD' },
  { id: 'altimeter', num: '05', label: 'Altimeter' },
  { id: 'helmet', num: '06', label: 'Helmet' },
  { id: 'wingsuit', num: '07', label: 'Wingsuit' },
];

const CALCULATOR_MAP = {
  container: ContainerCalculator,
  'main-canopy': MainCanopyCalculator,
  reserve: ReserveCalculator,
  aad: AADCalculator,
  altimeter: AltimeterCalculator,
  helmet: HelmetCalculator,
  wingsuit: WingsuitCalculator,
};

function buildPageSchema(rootURL) {
  return {
    '@type': 'WebApplication',
    name: 'Avento Skydiving Gear Value Calculator',
    url: `${rootURL}/gear-value-calculator`,
    description:
      'Free calculator estimating the fair market value of used skydiving gear based on age, usage, brand, and condition. Covers containers, main canopies, reserves, AADs, altimeters, helmets, and wingsuits.',
    applicationCategory: 'UtilityApplication',
    applicationSubCategory: 'Skydiving Gear Pricing Tool',
    operatingSystem: 'Web',
    browserRequirements: 'Requires JavaScript',
    inLanguage: 'en',
    featureList: [
      'Container pricing (UPT, Sun Path, Mirage, Aerodyne, Wings, Paratec, Rigging Innovation, SWS, Firebird)',
      'Main canopy pricing by brand, model, size, and jump count',
      'Reserve canopy pricing with TSO/age-based depreciation',
      'AAD pricing with battery and service-life adjustments',
      'Altimeter, helmet, and wingsuit valuations',
    ],
    audience: {
      '@type': 'Audience',
      audienceType:
        'Skydivers pricing used gear for sale or evaluating purchase offers',
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
    },
    author: {
      '@type': 'Organization',
      '@id': `${rootURL}#organization`,
      name: 'Avento Gear GmbH',
      url: rootURL,
    },
  };
}

const layoutAreas = `
  topbar
  main
  footer
`;

const GearValueCalculatorPageComponent = props => {
  const { scrollingDisabled } = props;
  const config = useConfiguration();

  const schemaTitle = 'Skydiving Gear Value Calculator | Avento';
  const schemaDescription =
    'Estimate the fair market price of used skydiving gear quickly. Calculators for containers, main canopies, reserves, AADs, altimeters, and helmets.';

  const rootURL = config.marketplaceRootURL || '';
  const pageSchema = buildPageSchema(rootURL);
  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${rootURL}/` },
      { '@type': 'ListItem', position: 2, name: 'Gear Value Calculator', item: `${rootURL}/gear-value-calculator` },
    ],
  };

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
      schema={[pageSchema, breadcrumbSchema]}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="GearValueCalculatorPage" />
              </Topbar>
              <Main as="main" className={css.main}>

                {/* Hero */}
                <PageHero
                  title="Gear Value Calculator"
                  subtitle="Estimate the fair market value of used skydiving gear based on age, usage, brand, and condition"
                  desktopImage={`${IMG}/hero-desktop.webp`}
                  desktopWidth={3024}
                  desktopHeight={700}
                  mobileImage={`${IMG}/hero-mobile.webp`}
                  mobileWidth={780}
                  mobileHeight={571}
                />

                {/* Introduction */}
                <section className={css.section}>
                  <div className={css.container}>
                    <h2 className={css.sectionHeading}>How It Works</h2>
                    <p className={css.paragraph}>
                      Pick a gear category and enter the details. The calculator uses
                      depreciation curves calibrated with market data, manufacturer pricing,
                      and real transaction history to estimate what your gear is worth today.
                    </p>
                    <p className={css.paragraph}>
                      If your specific model is not in our database, check "My model is not
                      listed" and enter the new price manually. The calculator will still
                      apply age and usage depreciation, but will skip brand-specific
                      adjustments.
                    </p>
                    <p className={css.betaNote}>
                      We are still fine-tuning the calculator. The results are solid
                      estimates but might not be perfectly accurate for every model and
                      condition yet.
                    </p>
                  </div>
                </section>

                {/* Jump nav */}
                <div className={css.tabNavWrapper}>
                  <nav className={css.tabNav} aria-label="Equipment categories">
                    {CATEGORIES.map(cat => (
                      <a
                        key={cat.id}
                        href={`#${cat.id}`}
                        className={css.tabItem}
                        onClick={e => {
                          e.preventDefault();
                          const el = document.getElementById(cat.id);
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                      >
                        <span className={css.tabNumber}>{cat.num}</span> {cat.label}
                      </a>
                    ))}
                  </nav>
                </div>

                {/* All calculators stacked */}
                <section className={css.calculatorSection}>
                  <div className={css.container}>
                    {CATEGORIES.map(cat => {
                      const Calculator = CALCULATOR_MAP[cat.id];
                      return (
                        <div key={cat.id} className={css.calculatorBlock} id={cat.id}>
                          <h3 className={css.categoryTitle}>
                            <span className={css.categoryNumber}>{cat.num}</span>
                            {cat.label}
                          </h3>
                          <Calculator />
                        </div>
                      );
                    })}
                  </div>
                </section>

              </Main>
              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const GearValueCalculatorPage = connect(mapStateToProps)(GearValueCalculatorPageComponent);

export default GearValueCalculatorPage;
